import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MdCheckCircle, MdDoneAll, MdFilterList } from "react-icons/md";
import { MdNotificationsNone } from "react-icons/md";
import Card from "components/card";
import ActionButton from "components/ui/ActionButton";
import EmptyState from "components/ui/EmptyState";
import SectionHeader from "components/ui/SectionHeader";
import StatusBadge from "components/ui/StatusBadge";
import { useLanguage, type TranslateParams } from "context/LanguageContext";
import { useInAppNotifications } from "context/InAppNotificationContext";
import {
  isNotificationTypeFilter,
  NotificationFilterValue,
  type NotificationFilter,
} from "enums/notification";
import { notificationService } from "services/notificationService";
import { classMgmtService } from "services/admin/classMgmtService";
import { authService } from "services/authService";
import {
  buildSelfCheckinAttendanceUrl,
  isSelfCheckinNotificationForStudent,
} from "utils/attendanceNotification";
import {
  buildLeaveRequestsUrl,
  buildLeaveReviewedUrl,
  isLeaveRequestedNotification,
  isLeaveReviewedNotificationForStudent,
} from "utils/leaveRequestNotification";
import type { AppNotification, NotificationTypeEnum } from "types/domain";

type TranslateFn = (key: string, params?: TranslateParams) => string;

const PAGE_SIZE = 20;

// Unknown types fall back to the raw enum value at render.
const TYPE_LABEL_KEYS: Record<NotificationTypeEnum, string> = {
  SESSION_REMINDER: "notifications.type.SESSION_REMINDER",
  SESSION_CANCELLED: "notifications.type.SESSION_CANCELLED",
  SESSION_UPDATED: "notifications.type.SESSION_UPDATED",
  SESSION_REPORT_SUBMITTED: "notifications.type.SESSION_REPORT_SUBMITTED",
  SESSION_REPORT_UPDATED: "notifications.type.SESSION_REPORT_UPDATED",
  STAFF_CHECKED_IN: "notifications.type.STAFF_CHECKED_IN",
  STAFF_CHECKED_OUT: "notifications.type.STAFF_CHECKED_OUT",
  LEAVE_REQUESTED: "notifications.type.LEAVE_REQUESTED",
  LEAVE_APPROVED: "notifications.type.LEAVE_APPROVED",
  LEAVE_REJECTED: "notifications.type.LEAVE_REJECTED",
  GENERAL: "notifications.type.GENERAL",
};

const TYPE_TONES: Record<
  NotificationTypeEnum,
  "info" | "danger" | "warning" | "success" | "neutral"
> = {
  SESSION_REMINDER: "info",
  SESSION_CANCELLED: "danger",
  SESSION_UPDATED: "warning",
  SESSION_REPORT_SUBMITTED: "success",
  SESSION_REPORT_UPDATED: "info",
  STAFF_CHECKED_IN: "success",
  STAFF_CHECKED_OUT: "neutral",
  LEAVE_REQUESTED: "warning",
  LEAVE_APPROVED: "success",
  LEAVE_REJECTED: "danger",
  GENERAL: "neutral",
};

const REPORT_TYPES: NotificationTypeEnum[] = [
  "SESSION_REPORT_SUBMITTED",
  "SESSION_REPORT_UPDATED",
];

const SESSION_TYPES: NotificationTypeEnum[] = [
  "SESSION_REMINDER",
  "SESSION_CANCELLED",
  "SESSION_UPDATED",
];

const STAFF_ATTENDANCE_TYPES: NotificationTypeEnum[] = [
  "STAFF_CHECKED_IN",
  "STAFF_CHECKED_OUT",
];

const timeAgo = (isoDate: string, t: TranslateFn): string => {
  const diff = Date.now() - new Date(isoDate).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return t("relativeTime.justNow");
  if (minutes < 60) return t("relativeTime.minutesAgo", { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t("relativeTime.hoursAgo", { count: hours });
  const days = Math.floor(hours / 24);
  return t("relativeTime.daysAgo", { count: days });
};

const formatDate = (isoDate: string, locale: string): string =>
  new Date(isoDate).toLocaleDateString(locale, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

export default function NotificationsPage() {
  const { t, locale } = useLanguage();
  const { unreadCount, markAllAsRead, refresh } = useInAppNotifications();
  const navigate = useNavigate();
  const location = useLocation();

  const layoutPrefix = location.pathname.startsWith("/systemadmin")
    ? "/systemadmin"
    : location.pathname.startsWith("/user")
    ? "/user"
    : "/admin";

  const [items, setItems] = useState<AppNotification[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<NotificationFilter>(
    NotificationFilterValue.ALL
  );
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const handleNotificationClick = useCallback(
    async (notif: AppNotification) => {
      if (!notif.is_read) {
        try {
          await notificationService.markAsRead(notif.id);
          setItems((prev) =>
            prev.map((n) => (n.id === notif.id ? { ...n, is_read: true } : n))
          );
          refresh();
        } catch {
          // ignore mark-read failures
        }
      }

      const classId = notif.metadata?.class_id as string | undefined;

      // Student's own check-in/check-out → their attendance-history page
      // with the matching record's detail modal auto-opened. Must run
      // before the staff branch below, which routes to a staff-only page.
      const currentUserRole = authService.getStoredUser()?.role;
      if (isSelfCheckinNotificationForStudent(notif, currentUserRole)) {
        navigate(buildSelfCheckinAttendanceUrl(notif));
        return;
      }

      // Student's leave request reviewed (approved/rejected) → their classes
      // page (there's no deep-link into SessionDetailModal outside that flow
      // today).
      if (isLeaveReviewedNotificationForStudent(notif, currentUserRole)) {
        navigate(buildLeaveReviewedUrl());
        return;
      }

      // Class manager receiving a new leave request → the review screen,
      // pre-filtered to the class from the notification's metadata.
      if (isLeaveRequestedNotification(notif)) {
        navigate(buildLeaveRequestsUrl(notif, layoutPrefix));
        return;
      }

      // Report notifications → session reports page
      if (REPORT_TYPES.includes(notif.type) && notif.session_id) {
        navigate(`${layoutPrefix}/sessions/${notif.session_id}/reports`);
        return;
      }

      // Session reminder/cancelled/updated → session detail page
      if (SESSION_TYPES.includes(notif.type) && notif.session_id && classId) {
        navigate(
          `${layoutPrefix}/classes-mgmt/${classId}/sessions/${notif.session_id}`
        );
        return;
      }

      // Staff check-in/check-out → session detail page. The notification
      // doesn't carry class_id in its metadata, so look it up from the session.
      if (STAFF_ATTENDANCE_TYPES.includes(notif.type) && notif.session_id) {
        try {
          const session = await classMgmtService.getSessionById(
            notif.session_id
          );
          if (session.class_id) {
            navigate(
              `${layoutPrefix}/classes-mgmt/${session.class_id}/sessions/${notif.session_id}`
            );
            return;
          }
        } catch {
          // fall through to expand-in-place below
        }
      }

      setExpandedId((prev) => (prev === notif.id ? null : notif.id));
    },
    [navigate, refresh, layoutPrefix]
  );

  const fetchPage = useCallback(
    async (pageNum: number, activeFilter: NotificationFilter) => {
      setLoading(true);
      try {
        const unreadOnly = activeFilter === NotificationFilterValue.UNREAD;
        const res = await notificationService.getMyNotifications(
          pageNum * PAGE_SIZE,
          PAGE_SIZE,
          unreadOnly
        );
        let filtered = res.items;
        if (isNotificationTypeFilter(activeFilter)) {
          filtered = res.items.filter((n) => n.type === activeFilter);
        }
        setItems(filtered);
        setTotal(
          isNotificationTypeFilter(activeFilter) ? filtered.length : res.total
        );
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchPage(page, filter);
  }, [page, filter, fetchPage]);

  const handleMarkAsRead = async (id: string) => {
    try {
      await notificationService.markAsRead(id);
      setItems((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      );
      refresh();
    } catch {
      // ignore
    }
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
    setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const filters: { key: NotificationFilter; label: string }[] = [
    { key: NotificationFilterValue.ALL, label: t("notif.filterAll") },
    { key: NotificationFilterValue.UNREAD, label: t("notif.filterUnread") },
    {
      key: NotificationFilterValue.SESSION_REMINDER,
      label: t("notif.filterReminder"),
    },
    {
      key: NotificationFilterValue.SESSION_CANCELLED,
      label: t("notif.filterCancelled"),
    },
    {
      key: NotificationFilterValue.SESSION_UPDATED,
      label: t("notif.filterUpdated"),
    },
    {
      key: NotificationFilterValue.SESSION_REPORT_SUBMITTED,
      label: t("notifications.filterNewReports"),
    },
    {
      key: NotificationFilterValue.SESSION_REPORT_UPDATED,
      label: t("notifications.filterUpdatedReports"),
    },
    { key: NotificationFilterValue.GENERAL, label: t("notif.filterGeneral") },
  ];

  return (
    <div className="mt-3">
      <SectionHeader
        title={t("notif.title")}
        description={t("notifications.pageDescription")}
        icon={<MdNotificationsNone className="h-6 w-6" />}
        actions={
          <>
            {unreadCount > 0 ? (
              <span className="flex h-9 min-w-[36px] items-center justify-center rounded-full bg-danger-600 px-3 text-xs font-bold text-white">
                {unreadCount}
              </span>
            ) : null}
            {unreadCount > 0 ? (
              <ActionButton
                startIcon={<MdDoneAll className="h-4 w-4" />}
                onClick={handleMarkAllAsRead}
              >
                {t("navbar.markAllRead")}
              </ActionButton>
            ) : null}
          </>
        }
      />

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <MdFilterList className="h-5 w-5 text-ink-muted" />
        {filters.map((f) => (
          <ActionButton
            key={f.key}
            onClick={() => {
              setFilter(f.key);
              setPage(0);
            }}
            variant={filter === f.key ? "primary" : "ghost"}
            size="sm"
            className={`rounded-full ${
              filter === f.key
                ? ""
                : "bg-surface-muted text-gray-600 hover:bg-surface-hover dark:text-gray-300"
            }`}
          >
            {f.label}
          </ActionButton>
        ))}
      </div>

      {/* Notification List */}
      <Card extra="p-0 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="border-t-transparent h-8 w-8 animate-spin rounded-full border-4 border-brand-500" />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<MdNotificationsNone className="h-12 w-12" />}
            title={t("navbar.noNotifications")}
            description={t("notifications.emptyDescription")}
            className="bg-transparent m-6 border-none shadow-none"
          />
        ) : (
          <div className="divide-y divide-line">
            {items.map((notif) => {
              const isExpanded = expandedId === notif.id;
              return (
                <div
                  key={notif.id}
                  className={`cursor-pointer transition-colors hover:bg-surface-hover ${ !notif.is_read ? "bg-blue-50/40 dark:bg-blue-900/5" : ""
                  }`}
                  onClick={() => handleNotificationClick(notif)}
                >
                  <div className="flex items-start gap-3 px-4 py-3 sm:px-6">
                    {/* Unread indicator */}
                    <div className="mt-2 flex-shrink-0">
                      <div
                        className={`h-2.5 w-2.5 rounded-full ${
                          !notif.is_read ? "bg-brand-500" : "bg-transparent"
                        }`}
                      />
                    </div>

                    {/* Content */}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-2">
                          <p
                            className={`text-sm font-semibold ${
                              !notif.is_read
                                ? "text-navy-700 dark:text-white"
                                : "text-ink-muted"
                            }`}
                          >
                            {notif.title}
                          </p>
                          <StatusBadge
                            label={
                              TYPE_LABEL_KEYS[notif.type]
                                ? t(TYPE_LABEL_KEYS[notif.type])
                                : notif.type
                            }
                            tone={TYPE_TONES[notif.type]}
                            className="px-2 py-0.5 text-[10px]"
                          />
                        </div>
                        <span className="whitespace-nowrap text-xs text-ink-subtle">
                          {timeAgo(notif.created_at, t)}
                        </span>
                      </div>

                      <p
                        className={`mt-1 text-sm ${
                          isExpanded ? "" : "line-clamp-2"
                        } ${
                          !notif.is_read
                            ? "text-ink-muted"
                            : "text-ink-muted"
                        }`}
                      >
                        {notif.message}
                      </p>

                      {isExpanded && (
                        <div className="mt-2 flex items-center gap-3 text-xs text-ink-subtle">
                          <span>{formatDate(notif.created_at, locale)}</span>
                          {notif.is_read && notif.read_at && (
                            <span className="flex items-center gap-1">
                              <MdCheckCircle className="h-3 w-3 text-green-500" />
                              {t("notif.readAt")}{" "}
                              {formatDate(notif.read_at, locale)}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Mark as read button */}
                    {!notif.is_read && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkAsRead(notif.id);
                        }}
                        className="mt-1 flex-shrink-0 rounded-md p-1 text-ink-subtle transition-colors hover:bg-surface-hover hover:text-brand-500"
                        title={t("notif.markRead")}
                      >
                        <MdCheckCircle className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-line px-4 py-3 sm:px-6">
            <p className="text-xs text-ink-muted">
              {t("notif.showing")} {page * PAGE_SIZE + 1}–
              {Math.min((page + 1) * PAGE_SIZE, total)} / {total}
            </p>
            <div className="flex gap-1">
              <button
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
                className="rounded-md px-3 py-1 text-sm font-medium text-gray-600 transition-colors hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-40 dark:text-gray-300"
              >
                {t("notif.prev")}
              </button>
              <button
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-md px-3 py-1 text-sm font-medium text-gray-600 transition-colors hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-40 dark:text-gray-300"
              >
                {t("notif.next")}
              </button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
